import { USDC_DECIMALS } from "@decibeltrade/sdk";

import { parseCCTPMessage, type ParsedCCTPMessage } from "./message";
import { parseCCTPMessageV2, type ParsedCCTPMessageV2 } from "./v2/message";

export type CCTPVersion = 1 | 2;

export type ParsedCCTPMessageAnyVersion =
  | ({ version: 1 } & ParsedCCTPMessage)
  | ({ version: 2 } & ParsedCCTPMessageV2);

// The header's version field: 0 for CCTP v1, 1 for CCTP v2.
const HEADER_VERSION_TO_CCTP: Record<number, CCTPVersion> = { 0: 1, 1: 2 };

/**
 * Which protocol a message belongs to, read from the message itself.
 * Dispatch claims and status checks on this, never on the v2 feature flag: a transfer started
 * under one version must finish under it even after the flag flips.
 */
export function getCCTPMessageVersion(messageHex: string): CCTPVersion | null {
  const hex = messageHex.startsWith("0x") ? messageHex.slice(2) : messageHex;
  const versionHex = hex.slice(0, 8);
  if (!/^[0-9a-fA-F]{8}$/.test(versionHex)) return null;
  return HEADER_VERSION_TO_CCTP[parseInt(versionHex, 16)] ?? null;
}

export function parseCCTPMessageAnyVersion(messageHex: string): ParsedCCTPMessageAnyVersion | null {
  switch (getCCTPMessageVersion(messageHex)) {
    case 1: {
      const parsed = parseCCTPMessage(messageHex);
      return parsed && { version: 1, ...parsed };
    }
    case 2: {
      const parsed = parseCCTPMessageV2(messageHex);
      return parsed && { version: 2, ...parsed };
    }
    default:
      return null;
  }
}

/** What the transfer UI needs from a message, the same shape for either version. */
export interface CCTPTransferFields {
  version: CCTPVersion;
  sourceDomain: number;
  sourceChain: string | null;
  destDomain: number;
  mintRecipient: string;
  /** USDC the recipient is minted: for v2, the burned amount minus the fee Circle took. */
  amount: number;
}

export function getCCTPTransferFields(messageHex: string): CCTPTransferFields | null {
  const parsed = parseCCTPMessageAnyVersion(messageHex);
  if (!parsed) return null;
  const { version, sourceDomain, sourceChain, destDomain, mintRecipient } = parsed;
  const amount =
    parsed.version === 1
      ? parsed.amount
      : Number(parsed.amount - parsed.feeExecuted) / 10 ** USDC_DECIMALS;
  return { version, sourceDomain, sourceChain, destDomain, mintRecipient, amount };
}
